import { lazy } from 'react';
import { matchPath } from 'react-router-dom';
import camelCase from 'lodash/camelCase';

import t from '@guestyci/localize/t.macro';

import metricTypes from '~/analytics/constants/metricTypes';

import {
  OWN_1099,
  USE_ACCOUNTING_FLOW,
  OWN_DASHBOARD,
} from 'constants/featureToggle';
import { LoginPageModes } from 'constants/loginPageModes';

const AuthPageWrapper = lazy(() => import('pages/AuthPageWrapper'));
const AnalyticsPageWrapper = lazy(() => import('pages/AnalyticsPageWrapper'));
const AccountingPageWrapper = lazy(() => import('pages/AccountingPageWrapper'));
const DocumentsPageWrapper = lazy(() => import('pages/DocumentsPageWrapper'));
const DashboardPageWrapper = lazy(() => import('pages/DashboardPageWrapper'));
const MyProfilePageWrapper = lazy(() => import('pages/MyProfilePageWrapper'));
const MyPropertiesPageWrapper = lazy(
  () => import('pages/MyPropertiesPageWrapper')
);
const ReservationReportPageWrapper = lazy(
  () => import('pages/ReservationReportPageWrapper')
);
const HelpCenterPageWrapper = lazy(() => import('pages/HelpCenterPageWrapper'));

const conditions = {
  always: () => true,
  never: () => false,
  fetchingPublic: (state) => state.ui.isMetaLoading,
  fetchingProtected: (state) => state.ui.isMetaLoading || !state.user,
  dashboard: () => window.featureToggle?.isEnabled(OWN_DASHBOARD),
  analytics: ({ ownersPortalSettings }) =>
    Object.keys(metricTypes).some(
      (type) =>
        ownersPortalSettings[type] === true ||
        ownersPortalSettings[camelCase(type)] === true
    ),
  reservationReport: ({ ownersPortalSettings }) =>
    ownersPortalSettings.guestsReports,
  accounting: () =>
    window.featureToggle?.isEnabled(USE_ACCOUNTING_FLOW) ||
    window.featureToggle?.isEnabled(OWN_1099),
  helpCenter: ({ ownersPortalSettings }) => ownersPortalSettings.showHelpCenter,
};

const publicRoutes = [
  {
    getName: () => t('Login'),
    path: '/login',
    component: AuthPageWrapper,
    isFetching: conditions.fetchingPublic,
    isAllowed: conditions.always,
    showInNavBar: conditions.never,
    initState: LoginPageModes.LOGIN,
  },
  {
    getName: () => t('Register'),
    path: '/register',
    component: AuthPageWrapper,
    isFetching: conditions.fetchingPublic,
    isAllowed: conditions.always,
    showInNavBar: conditions.never,
    initState: LoginPageModes.REGISTER,
  },
  {
    getName: () => t('Reset password'),
    path: '/login/reset',
    component: AuthPageWrapper,
    isFetching: conditions.fetchingPublic,
    isAllowed: conditions.always,
    showInNavBar: conditions.never,
    initState: LoginPageModes.NEW_PASSWORD,
  },
  {
    getName: () => t('Forgot password'),
    path: '/forgot-password',
    component: AuthPageWrapper,
    isFetching: conditions.fetchingPublic,
    isAllowed: conditions.always,
    showInNavBar: conditions.never,
    initState: LoginPageModes.FORGOT_PASSWORD,
  },
];

const protectedRoutes = [
  {
    getName: () => t('Dashboard'),
    path: '/dashboard',
    component: DashboardPageWrapper,
    isFetching: conditions.fetchingProtected,
    isAllowed: conditions.dashboard,
    showInNavBar: conditions.dashboard,
  },
  {
    getName: () => t('My properties'),
    path: '/my-properties',
    component: MyPropertiesPageWrapper,
    isFetching: conditions.fetchingProtected,
    isAllowed: conditions.always,
    showInNavBar: conditions.always,
  },
  {
    getName: () => t('My profile'),
    path: '/my-profile',
    component: MyProfilePageWrapper,
    isFetching: conditions.fetchingProtected,
    isAllowed: conditions.always,
    showInNavBar: conditions.never,
  },
  {
    getName: () => t('Analytics'),
    path: '/analytics',
    component: AnalyticsPageWrapper,
    isFetching: conditions.fetchingProtected,
    isAllowed: conditions.analytics,
    showInNavBar: conditions.analytics,
  },
  {
    getName: () => t('Reservation report'),
    path: '/reservation-report',
    component: ReservationReportPageWrapper,
    isFetching: conditions.fetchingProtected,
    isAllowed: conditions.reservationReport,
    showInNavBar: conditions.reservationReport,
  },
  {
    getName: () => t('Documents'),
    path: '/documents',
    component: DocumentsPageWrapper,
    isFetching: conditions.fetchingProtected,
    isAllowed: conditions.always,
    showInNavBar: conditions.always,
  },
  {
    getName: () => t('Accounting'),
    path: '/accounting',
    component: AccountingPageWrapper,
    isFetching: conditions.fetchingProtected,
    isAllowed: conditions.accounting,
    showInNavBar: conditions.accounting,
  },
  {
    getName: () => t('Help center'),
    path: '/help-center',
    component: HelpCenterPageWrapper,
    isFetching: conditions.fetchingProtected,
    isAllowed: conditions.helpCenter,
    showInNavBar: conditions.helpCenter,
  },
];

const allRoutes = [...publicRoutes, ...protectedRoutes];

const filterAllowedRoutes = (routes, params) =>
  routes.filter((route) => route.isAllowed(params));

export const getPublicRoutes = ({ ownersPortalSettings = {} }) =>
  filterAllowedRoutes(publicRoutes, { ownersPortalSettings });

export const getProtectedRoutes = ({ ownersPortalSettings = {} }) =>
  filterAllowedRoutes(protectedRoutes, { ownersPortalSettings });

export const getMatchingRoute = (pathname) => {
  for (let i = 0, ii = allRoutes.length; i < ii; i += 1) {
    const route = allRoutes[i];
    const match = matchPath(pathname, {
      path: route.path,
      exact: true,
    });

    if (match) {
      return { ...route, match };
    }
  }

  return undefined;
};

export const getNavItems = ({ ownersPortalSettings = {} }) => {
  return allRoutes
    .filter((navItem) => navItem.showInNavBar({ ownersPortalSettings }))
    .map((item) => ({
      getName: item.getName,
      href: item.path,
    }));
};
